import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { handleApi } from "../../apis";
import { GET_SEARCH_BILL_DETAILS, GET_BILL_PDF_STATUS } from "../../apis/configuration";
import { LanguageContext } from "../../context/LanguageContext";

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;
const PENDING_STATUSES = ["PENDING", "PROCESSING", "IN_PROGRESS"];
export const PDF_STATUS = { PROCESSING: "processing", DONE: "done", FAILED: "failed" };

const extractErrorMessage = (err, fallback) => err?.response?.data?.message || fallback;


const useBillDetails = () => {
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [billPdfUrl, setBillPdfUrl] = useState("");
  const [billDetails, setBillDetails] = useState(null);
  const [pdfStatus, setPdfStatus] = useState(PDF_STATUS.PROCESSING);
  const [billingKey, setBillingKey] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const pollTimeoutIdRef = useRef(null);

  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => clearTimeout(pollTimeoutIdRef.current);
  }, []);

  // Resolves true as soon as the bill details arrive, even while the PDF is still processing, so
  // the page can show them. Polling then continues in the background until the PDF is DONE,
  // FAILED or the timeout; only without bill details do those end the search with an error.
  const pollForPdf = useCallback(
    (billingKey, accessToken, requestId, startedAt) =>
      new Promise((resolve) => {
        let hasDetails = false;

        const stopPdf = (message) => {
          if (hasDetails) {
            setPdfStatus(PDF_STATUS.FAILED);
          } else {
            setError(message || I18n.something_went_wrong);
          }
          resolve(hasDetails);
        };

        const attempt = async () => {
          if (requestIdRef.current !== requestId) {
            resolve(false);
            return;
          }
          if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
            stopPdf();
            return;
          }

          try {
            const response = await handleApi(
              GET_BILL_PDF_STATUS,
              { billing_key: billingKey, access_token: accessToken },
              null,
              navigate,
            );
            if (requestIdRef.current !== requestId) {
              resolve(false);
              return;
            }

            if (!hasDetails && (response?.bill_amount != null || response?.consumer_number)) {
              hasDetails = true;
              setBillDetails({
                amount: response.bill_amount,
                amountLate: response.bill_amount_late,
                dueDate: response.bill_deadline,
                status: response.bill_status,
                consumerNumber: response.consumer_number,
                consumerName: response.consumer_name,
                billerLogo: response.biller_logo,
              });
              resolve(true);
            }

            if (response?.pdf?.status === "DONE" && response?.pdf?.url) {
              setBillPdfUrl(response.pdf.url);
              setPdfStatus(PDF_STATUS.DONE);
              resolve(hasDetails);
              return;
            }

            // 202 responses come back as { success: false, pdf_status: "PENDING" } while the
            // bill inquiry is still processing — keep polling instead of treating it as an error.
            const pdfStatus = response?.pdf?.status || response?.pdf_status;
            if (PENDING_STATUSES.includes(pdfStatus)) {
              pollTimeoutIdRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
              return;
            }

            if (response?.success === false || pdfStatus === "FAILED") {
              stopPdf(response?.pdf?.message || response?.message);
              return;
            }

            pollTimeoutIdRef.current = setTimeout(attempt, POLL_INTERVAL_MS);
          } catch (err) {
            if (requestIdRef.current !== requestId) {
              resolve(false);
              return;
            }
            stopPdf(err?.response?.data?.message);
          }
        };

        attempt();
      }),
    [navigate, I18n],
  );

  const searchBill = useCallback(
    async (payload) => {
      const requestId = ++requestIdRef.current;
      clearTimeout(pollTimeoutIdRef.current);
      setLoading(true);
      setError("");
      setBillPdfUrl("");
      setBillDetails(null);
      setPdfStatus(PDF_STATUS.PROCESSING);

      try {
        const response = await handleApi(GET_SEARCH_BILL_DETAILS, payload, null, navigate);
        if (requestIdRef.current !== requestId) return false;

        if (!response?.success || !response?.billing_key) {
          setError(response?.message || I18n.something_went_wrong);
          return false;
        }

        setBillingKey(response.billing_key);
        setAccessToken(response.access_token);
        return await pollForPdf(response.billing_key, response.access_token, requestId, Date.now());
      } catch (err) {
        if (requestIdRef.current !== requestId) return false;
        setError(extractErrorMessage(err, I18n.something_went_wrong));
        return false;
      } finally {
        if (requestIdRef.current === requestId) setLoading(false);
      }
    },
    [navigate, I18n, pollForPdf],
  );

  const resetBill = useCallback(() => {
    requestIdRef.current += 1;
    clearTimeout(pollTimeoutIdRef.current);
    setBillPdfUrl("");
    setBillDetails(null);
    setPdfStatus(PDF_STATUS.PROCESSING);
    setBillingKey(null);
    setAccessToken(null);
    setError("");
    setLoading(false);
  }, []);

  return {
    loading,
    error,
    billPdfUrl,
    billDetails,
    pdfStatus,
    billingKey,
    accessToken,
    searchBill,
    resetBill,
  };
};

export default useBillDetails;
